import React from 'react';
import { Flex, styled } from '@withjoy/joykit';

import { CustomComponentEmptyState, CustomComponentErrorState } from './components';
import { useCustomData } from './hooks/useCustomData';
import { sortBySortOrder } from '@shared/utils/array';
import { filterInvalidComponents } from './utils/filterInvalidComponents';
import type { JsonDataBlobComponentFragment } from '../../../../../admin/routes/WebsiteEditor/features/custom-page/WebsiteEditorCustomPage.graphql.generated';
import type {
  GuestMediaGalleryComponentFragment,
  GuestPhotoComponentFragment,
  GuestTextComponentFragment,
  GuestTravelMapComponentFragment,
  GuestVideoComponentFragment,
} from './Custom.graphql.generated';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { pxToRem } from '@withjoy/joykit/theme';
import { GuestSiteCustomJsonComponentWrapper } from './components/GuestSiteCustomJsonComponentWrapper';

import { LazyRichText } from './components/RichText/RichText.lazy';
import { LazyAccommodations } from './components/Accommodations/Accommodations.lazy';
import { LazyPhoto } from './components/Photo/Photo.lazy';
import { LazyVideo } from './components/Video/Video.lazy';
import { LazyPhotoGallery } from './components/PhotoGallery/PhotoGallery.lazy';
interface Props {
  pageId: string;
  eventId: string;
  pageSlug: string;
  eventHandle: string;
  pageTitle?: string;
  subTitle?: string | null;
  isBrannanLayout?: boolean;
  pages?: readonly EventPageFragment[];
}

const ComponentsContainer = styled(Flex)`
  flex-wrap: wrap;
  gap: ${pxToRem(48)};
  align-items: stretch;
  width: 100%;
  justify-content: flex-start;
`;

export const Custom: React.FC<Props> = props => {
  const { pageSlug, eventId, eventHandle, isBrannanLayout, pages } = props;
  const { loading, pageContainerBySlug, error } = useCustomData(eventId, eventHandle, pageSlug);

  const [isPhotoGalleryCustomBlockEnabled] = useIsFeatureEnabled('photoGalleryCustomBlockEnabled');

  const hasCustomComponents = pageContainerBySlug?.components ? pageContainerBySlug.components.length > 0 : false;

  const sortedComponents = sortBySortOrder(pageContainerBySlug?.components?.filter(filterInvalidComponents) ?? []);

  const customComponents = sortedComponents
    .map(componentData => {
      switch (componentData.__typename) {
        case 'TravelMapComponent':
          return (
            <LazyAccommodations
              key={componentData.id}
              {...props}
              travelMapComponentData={componentData as GuestTravelMapComponentFragment}
            />
          );
        case 'TextComponent':
          return (
            <LazyRichText
              key={componentData.id}
              isBrannanLayout={isBrannanLayout}
              richTextComponentData={componentData as GuestTextComponentFragment}
            />
          );
        case 'PhotoComponent':
          return (
            <LazyPhoto
              key={componentData.id}
              photoComponentData={componentData as GuestPhotoComponentFragment}
            />
          );
        case 'VideoComponent':
          return (
            <LazyVideo
              key={componentData.id}
              videoComponentData={componentData as GuestVideoComponentFragment}
            />
          );
        case 'MediaGalleryComponent':
          return isPhotoGalleryCustomBlockEnabled ? (
            <LazyPhotoGallery
              key={componentData.id}
              mediaGalleryComponentData={componentData as GuestMediaGalleryComponentFragment}
            />
          ) : null;
        case 'JsonDataBlobComponent':
          return (
            <GuestSiteCustomJsonComponentWrapper
              key={componentData.id}
              jsonComponentData={componentData as JsonDataBlobComponentFragment}
              pages={pages}
            />
          );
        default:
          return null;
      }
    })
    .filter((element): element is JSX.Element => element !== null);

  if (loading) {
    return <></>;
  }

  if (error) {
    return (
      <CustomComponentErrorState
        error={error}
        eventHandle={eventHandle}
      />
    );
  }

  return (
    <Flex
      flexDirection={'column'}
      width={'100%'}
      rowGap={pxToRem(45)}
      alignItems="center"
    >
      {hasCustomComponents ? (
        <ComponentsContainer flexDirection={['column', 'row']}>
          {customComponents.map((component, index) => (
            <React.Fragment key={`custom-components-guest-${index}`}>{component}</React.Fragment>
          ))}
        </ComponentsContainer>
      ) : (
        <CustomComponentEmptyState isBrannanLayout={isBrannanLayout} />
      )}
    </Flex>
  );
};
